import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Target, Handshake, MapPin, Gift, Ticket, Star, HeartHandshake, Users, Store, TrendingUp, BarChart3, ShieldCheck, Globe2 } from 'lucide-react';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-707" data-mp="c9pyntv02svw">
    {children}
  </motion.div>;
export function ParaMarca() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-708" data-mp="v8u64y6w8elg">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-709" data-mp="2tgipo33hijro"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-710" data-mp="2olwf22588v8n">
        <FadeIn data-id="element-711" data-mp="2amd8vevz7tyz">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-712" data-mp="2aq0xbzmkdq6u">
            <Target size={16} className="text-brand-yellow" data-id="element-713" data-mp="20r1z7azfdcy3" />
            {t.paraMarca.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight max-w-5xl mx-auto" data-id="element-714" data-mp="25p3l03o8jqd9">
            {t.paraMarca.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-715" data-mp="3wp1j061mduq">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-716" data-mp="2gl2m425vu0n">
                {t.paraMarca.heroTitle2}
              </span>
              {/* SVG Highlight */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-717" data-mp="3km7lctaqw89a">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-718" data-mp="g77rl4i4e5jj" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-719" data-mp="13xka19azrkod">
            {t.paraMarca.heroDesc}
          </p>
        </FadeIn>
      </section>

      {/* Community Trust Intro Section */}
      <section className="bg-white dark:bg-gray-800 py-20 px-6 relative z-10 transition-colors duration-300 border-y border-gray-100 dark:border-gray-700" data-id="element-720" data-mp="2hvoi6i9p1xke">
        <div className="max-w-4xl mx-auto text-center" data-id="element-721" data-mp="2qsyxcepmu6ul">
          <FadeIn data-id="element-722" data-mp="2vt3co1et0vj8">
            <div className="w-16 h-16 bg-brand-yellow/20 dark:bg-brand-yellow/10 text-brand-yellow-dark dark:text-brand-yellow rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-723" data-mp="2vtmm0tqe2bln">
              <ShieldCheck size={32} data-id="element-724" data-mp="35r0w8blpy45i" />
            </div>
            <p className="text-2xl md:text-3xl font-sans font-medium text-brand-charcoal dark:text-white leading-relaxed" data-id="element-725" data-mp="35pg6czacn9dz">
              {t.paraMarca.heroNielsen}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Active Territories */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-726" data-mp="3jjgvr2fevk1o">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-727" data-mp="3ohs7jip9o4mj">
          <FadeIn data-id="element-728" data-mp="3oi1v6zmu9zp2">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-729" data-mp="3offn8yw1di6b">
              {t.paraMarca.activeTerritoriesTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-10 leading-relaxed" data-id="element-730" data-mp="35u49m8yrz3a0">
              {t.paraMarca.activeTerritoriesDesc}
            </p>
            <ul className="space-y-6" data-id="element-731" data-mp="3fma17hl47ioo">
              {[{
              icon: <MapPin size={20} data-id="element-732" data-mp="2rwyxpscdra6u" />,
              text: t.paraMarca.activeTerritoriesList1
            }, {
              icon: <Gift size={20} data-id="element-733" data-mp="2mzgxcqcfi3ag" />,
              text: t.paraMarca.activeTerritoriesList2
            }, {
              icon: <Ticket size={20} data-id="element-734" data-mp="2wv0qx63ispw1" />,
              text: t.paraMarca.activeTerritoriesList3
            }, {
              icon: <Star size={20} data-id="element-735" data-mp="h2bbil55v8lh" />,
              text: t.paraMarca.activeTerritoriesList4
            }, {
              icon: <HeartHandshake size={20} data-id="element-736" data-mp="jou6rwlfalrr" />,
              text: t.paraMarca.activeTerritoriesList5
            }, {
              icon: <Users size={20} data-id="element-737" data-mp="op8a51c2x7jg" />,
              text: t.paraMarca.activeTerritoriesList6
            }].map((item, i) => <li key={i} className="flex items-center gap-4" data-id="element-738" data-mp="eoxyvm9lua6x" data-mpi={i}>
                  <div className="w-10 h-10 rounded-full bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center shrink-0" data-id="element-739" data-mp="erk7lp88evqq">
                    {item.icon}
                  </div>
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium text-lg" data-id="element-740" data-mp="tnjmpjtrdw5d">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
          <FadeIn delay={0.2} className="relative" data-id="element-741" data-mp="1lcd0lawouxbs">
            <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-742" data-mp="1q4ea4p2h29y4"></div>
            <div className="bg-white dark:bg-gray-800 p-10 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700" data-id="element-743" data-mp="1zzbsf6zgjtr1">
              <div className="grid grid-cols-2 gap-6" data-id="element-744" data-mp="1vwx4i8gxm3sw">
                <div className="bg-brand-cream dark:bg-gray-900 p-6 rounded-2xl text-center" data-id="element-745" data-mp="1qyvoxxipsmm6">
                  <MapPin className="mx-auto mb-4 text-brand-green" size={32} data-id="element-746" data-mp="1m1n75y1haklg" />
                  <p className="font-bold text-brand-charcoal dark:text-white" data-id="element-747" data-mp="1h2air8uyl5gr">
                    {t.paraMarca.formats[0]}
                  </p>
                </div>
                <div className="bg-brand-cream dark:bg-gray-900 p-6 rounded-2xl text-center" data-id="element-748" data-mp="1c8xyfaxbbktt">
                  <Gift className="mx-auto mb-4 text-brand-yellow" size={32} data-id="element-749" data-mp="187cnvw893ims" />
                  <p className="font-bold text-brand-charcoal dark:text-white" data-id="element-750" data-mp="13bxd5d5ji2ul">
                    {t.paraMarca.formats[1]}
                  </p>
                </div>
                <div className="bg-brand-cream dark:bg-gray-900 p-6 rounded-2xl text-center" data-id="element-751" data-mp="tbwpjf0n10kl">
                  <Ticket className="mx-auto mb-4 text-brand-green" size={32} data-id="element-752" data-mp="j9148b9h32en" />
                  <p className="font-bold text-brand-charcoal dark:text-white" data-id="element-753" data-mp="o72mwv2zkzpl">
                    {t.paraMarca.formats[2]}
                  </p>
                </div>
                <div className="bg-brand-cream dark:bg-gray-900 p-6 rounded-2xl text-center" data-id="element-754" data-mp="t4ckiy0j4hoi">
                  <HeartHandshake className="mx-auto mb-4 text-brand-yellow" size={32} data-id="element-755" data-mp="y4qs6ey7xpm0" />
                  <p className="font-bold text-brand-charcoal dark:text-white" data-id="element-756" data-mp="5gbj091rq1il">
                    {t.paraMarca.formats[3]}
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Strengthen Local Businesses */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-757" data-mp="3dwwlr83t3yd9">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-758" data-mp="3iu6g9261z07y"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-759" data-mp="wwt0o09v00y7">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-760" data-mp="1aviyji4i64nm">
            <FadeIn className="order-2 lg:order-1" data-id="element-761" data-mp="1asn0zuwkhhzp">
              <div className="bg-white/5 p-10 rounded-3xl border border-white/10 backdrop-blur-sm" data-id="element-762" data-mp="15v4yond2210o">
                <Store className="text-brand-yellow mb-6" size={48} data-id="element-763" data-mp="1jud0zp4ilp93" />
                <p className="text-2xl font-sans font-medium leading-relaxed italic" data-id="element-764" data-mp="1fum4r5w7ygx6">
                  "{t.paraMarca.strengthenConclusion}"
                </p>
              </div>
            </FadeIn>
            <FadeIn className="order-1 lg:order-2" data-id="element-765" data-mp="1olw63jdhfhcf">
              <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-white mb-6 tracking-tight" data-id="element-766" data-mp="1on62m44oshgy">
                {t.paraMarca.strengthenTitle}
              </h2>
              <p className="text-lg text-white/70 mb-10 leading-relaxed" data-id="element-767" data-mp="1ylvcb9mfivm4">
                {t.paraMarca.strengthenDesc}
              </p>
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-768" data-mp="2i4j59dgv9gzs">
                {[t.paraMarca.strengthenList1, t.paraMarca.strengthenList2, t.paraMarca.strengthenList3, t.paraMarca.strengthenList4].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-769" data-mp="2d93vawlzc58n" data-mpi={i}>
                    <CheckCircle2 className="text-brand-green shrink-0 mt-1" size={20} data-id="element-770" data-mp="1ygtnlz2n8x4a" />
                    <span className="font-medium" data-id="element-771" data-mp="1pi86m8ctlq5m">{item}</span>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Campaign Examples */}
      <section className="py-24 px-6 bg-white dark:bg-gray-800 relative z-10 transition-colors duration-300" data-id="element-772" data-mp="1fkjymgq7em5i">
        <div className="max-w-7xl mx-auto" data-id="element-773" data-mp="1khaeb2y3jfqe">
          <FadeIn className="text-center mb-16" data-id="element-774" data-mp="1kkg6ir20uxqc">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-775" data-mp="1ll7izzor7mj1">
              {t.paraMarca.examplesTitle}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-776" data-mp="1bpcpg9izs71h">
            <FadeIn delay={0.1} data-id="element-777" data-mp="1gmw43ajlqlty">
              <div className="bg-brand-cream dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 h-full" data-id="element-778" data-mp="1go7brlg64dqc">
                <div className="w-12 h-12 bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6" data-id="element-779" data-mp="24etgp3uvlhw4">
                  <MapPin size={24} data-id="element-780" data-mp="1ulvw8s6bpiuv" />
                </div>
                <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-781" data-mp="1zm0ascno83ig">
                  {t.paraMarca.example1Title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 mb-6 leading-relaxed" data-id="element-782" data-mp="2na1i5i945dwa">
                  {t.paraMarca.example1Desc}
                </p>
                <ul className="space-y-2" data-id="element-783" data-mp="gmslbvlkkiio">
                  {[t.paraMarca.example1List1, t.paraMarca.example1List2, t.paraMarca.example1List3, t.paraMarca.example1List4].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm font-medium text-brand-charcoal/80 dark:text-gray-200" data-id="element-784" data-mp="lditfgldqzh2" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow" data-id="element-785" data-mp="3ovar9bm76dn6"></div>
                      {item}
                    </li>)}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-786" data-mp="2nb35z2s43ia">
              <div className="bg-brand-cream dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 h-full" data-id="element-787" data-mp="2m9jx7bm0aph">
                <div className="w-12 h-12 bg-brand-yellow/20 text-brand-yellow-dark dark:text-brand-yellow rounded-xl flex items-center justify-center mb-6" data-id="element-788" data-mp="1e2q238ejt5g1">
                  <Gift size={24} data-id="element-789" data-mp="araivfcpn0wj" />
                </div>
                <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-790" data-mp="fqdph48c9f6m">
                  {t.paraMarca.example2Title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 mb-4 leading-relaxed" data-id="element-791" data-mp="knvoa1sn6e0w">
                  {t.paraMarca.example2Desc}
                </p>
                <p className="text-sm font-medium text-brand-charcoal/80 dark:text-gray-200 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-792" data-mp="3o7rqefv9st7t">
                  {t.paraMarca.example2Detail}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-793" data-mp="5szal922cygf">
              <div className="bg-brand-cream dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 h-full" data-id="element-794" data-mp="5uahhhqt2mbr">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center mb-6" data-id="element-795" data-mp="39hsvclz0hxwk">
                  <Globe2 size={24} data-id="element-796" data-mp="31xrotdh9ftf4" />
                </div>
                <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-797" data-mp="2wyovgqan7bmz">
                  {t.paraMarca.example3Title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-798" data-mp="2szpufgs17z0t">
                  {t.paraMarca.example3Desc}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-799" data-mp="2j0qjfrjsnran">
              <div className="bg-brand-cream dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 h-full" data-id="element-800" data-mp="2e8z2m8qfwzxp">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl flex items-center justify-center mb-6" data-id="element-801" data-mp="29anq1q8rgbbs">
                  <HeartHandshake size={24} data-id="element-802" data-mp="208y0yr2cpoh6" />
                </div>
                <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-803" data-mp="208eeqy5o4dxc">
                  {t.paraMarca.example4Title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 mb-4 leading-relaxed" data-id="element-804" data-mp="3vbudz14pb3jt">
                  {t.paraMarca.example4Desc}
                </p>
                <p className="text-sm font-medium text-brand-charcoal/80 dark:text-gray-200 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-805" data-mp="47m7m2bsgk3j">
                  {t.paraMarca.example4Detail}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-806" data-mp="3go9ah63wz2bd">
        <FadeIn className="text-center mb-16" data-id="element-807" data-mp="32nosud9qjcjg">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-808" data-mp="32ozwmd36kkak">
            {t.paraMarca.benefitsTitle}
          </h2>
        </FadeIn>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-809" data-mp="10wlg9igabnw9">
          {[{
          icon: <Target size={24} data-id="element-810" data-mp="15udjh8mdqwfr" />,
          title: t.paraMarca.benefit1Title,
          desc: t.paraMarca.benefit1Desc
        }, {
          icon: <ShieldCheck size={24} data-id="element-811" data-mp="2lew25it5zs1n" />,
          title: t.paraMarca.benefit2Title,
          desc: t.paraMarca.benefit2Desc
        }, {
          icon: <Handshake size={24} data-id="element-812" data-mp="2vcacd0ohvkli" />,
          title: t.paraMarca.benefit3Title,
          desc: t.paraMarca.benefit3Desc
        }, {
          icon: <Globe2 size={24} data-id="element-813" data-mp="391ueq5e5yfua" />,
          title: t.paraMarca.benefit4Title,
          desc: t.paraMarca.benefit4Desc
        }].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-814" data-mp="3j0jxe0dvw4bl" data-mpi={i}>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 h-full" data-id="element-815" data-mp="3e2s5h6d8oi7e">
                <div className="w-12 h-12 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6" data-id="element-816" data-mp="1on27w7tx4xdx">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-817" data-mp="29epia4ctedeu">
                  {benefit.title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-818" data-mp="2id1huvvnsljf">
                  {benefit.desc}
                </p>
              </div>
            </FadeIn>)}
          <FadeIn delay={0.4} className="md:col-span-2 lg:col-span-1" data-id="element-819" data-mp="2sca9hi31xo2m">
            <div className="bg-brand-green-dark p-8 rounded-3xl shadow-sm border border-brand-green-dark/20 h-full text-white" data-id="element-820" data-mp="2s9xt39cxzlro">
              <div className="w-12 h-12 bg-white/10 text-brand-yellow rounded-xl flex items-center justify-center mb-6" data-id="element-821" data-mp="313kblxs52six">
                <BarChart3 size={24} data-id="element-822" data-mp="3623yq1igyacv" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-823" data-mp="3b28ic0eobl7b">
                {t.paraMarca.benefit5Title}
              </h3>
              <p className="text-white/80 mb-4 leading-relaxed" data-id="element-824" data-mp="3g0tgluad7ut4">
                {t.paraMarca.benefit5Desc}
              </p>
              <ul className="space-y-2" data-id="element-825" data-mp="14eraysihbwlo">
                {[t.paraMarca.benefit5List1, t.paraMarca.benefit5List2, t.paraMarca.benefit5List3, t.paraMarca.benefit5List4, t.paraMarca.benefit5List5].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm font-medium text-white/90" data-id="element-826" data-mp="pijr5tazgx6x" data-mpi={i}>
                    <CheckCircle2 size={16} className="text-brand-yellow" data-id="element-827" data-mp="1i54ln2mr9pep" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-green-dark text-white py-32 px-6 text-center relative overflow-hidden" data-id="element-828" data-mp="18dqov2m6v3p9">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-829" data-mp="25wtdyizkudvn"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-830" data-mp="1e7dz40cds33m"></div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-831" data-mp="15eu6bgirssqp">
          <FadeIn data-id="element-832" data-mp="15fbz23684otv">
            <h2 className="text-4xl md:text-6xl font-sans font-extrabold text-white mb-8 tracking-tight" data-id="element-833" data-mp="15cpr42ff87b4">
              {t.paraMarca.ctaTitle}
            </h2>
            <p className="text-xl text-white/80 mb-6 leading-relaxed" data-id="element-834" data-mp="1x4yqpf6j5frx">
              {t.paraMarca.ctaDesc}
            </p>
            <p className="text-lg text-white/60 mb-12 leading-relaxed italic" data-id="element-835" data-mp="1j8l6hyam309r">
              {t.paraMarca.ctaDesc2}
            </p>
            <div className="flex flex-col items-center gap-6" data-id="element-836" data-mp="7rldt1kcqjf7">
              <Link to="/contato" className="bg-brand-yellow hover:bg-brand-yellow-dark text-brand-charcoal px-10 py-4 rounded-full text-base font-bold transition-all shadow-sm hover:shadow-md flex items-center gap-2 group" data-id="element-837" data-mp="7q0n5n15rkmm">
                {t.paraMarca.ctaButton}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-838" data-mp="a5d0t60rf0s" />
              </Link>
              <p className="text-white/50 text-sm font-medium" data-id="element-839" data-mp="3hoaiz6phdi8p">
                {t.paraMarca.ctaOr}{' '}
                <a href="mailto:jon@bairrouapp.com.br" className="font-bold text-white hover:text-brand-yellow transition-colors" data-id="element-840" data-mp="3dmp79ooona03">
                  jon@bairrouapp.com.br
                </a>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>;
}